import { Text } from "@ecco/ui/text";
import {
    Accordion,
    AccordionButton,
    AccordionIcon,
    AccordionItem,
    AccordionPanel,
} from "@ecco/ui/accordion";
import { Box } from "@ecco/ui/box";
import { Flex } from "@ecco/ui/flex";
import { styles } from "./PromoCodeRow.styles";
import { PromoCode } from "./components/promo-code";
import { PromoInput } from "./components/promo-input";
import { useGetConfig } from "./hooks/useGetConfig";
import { useEffect, useState } from "react";

interface Props {
    setCartOrSessionChanged?: (cartOrSessionChanged: boolean) => void;
    cartOrSessionChanged?: boolean;
    locale: string;
    usedDiscountCodeIds?: string[];
    /** Overrides the accordion label. Checkout V2 words this differently; omit for V1's copy. */
    title?: string;
}

const PromoCodeRow = ({
    cartOrSessionChanged,
    setCartOrSessionChanged,
    locale,
    usedDiscountCodeIds,
    title,
}: Props) => {
    const {
        applyErrorMessage,
        countryISOCode,
        defaultIndex,
        headers,
        isApplying,
        addedPromocodesErrors,
        setDraftPromoCode,
        isPromoRemoving,
        discountCharsLimit,
        onApply,
        onRemove,
        getIsRemoving,
        getPromoCodeDiscountValue,
        validPromoCodes,
        shouldShowCodeInput,
        isLocked,
        setAddedPromocodesErrors,
    } = useGetConfig({
        cartOrSessionChanged,
        setCartOrSessionChanged,
    });

    const [duplicatePromoCodes, setDuplicatePromoCodes] = useState<string[]>([]);

    useEffect(() => {
        if (!usedDiscountCodeIds?.length) {
            setDuplicatePromoCodes([]);
            return;
        }

        // Create a map to track which discount IDs have been seen
        const seenCartDiscountIds: Record<string, string> = {};
        const newDuplicates: string[] = [];

        // Check each promo code
        validPromoCodes.forEach((item) => {
            const cartDiscountId = item.discountCode.cartDiscounts[0]?.id;
            const promoCodeId = item.discountCode.id;

            if (cartDiscountId && usedDiscountCodeIds.includes(cartDiscountId)) {
                if (seenCartDiscountIds[cartDiscountId]) {
                    // This is a duplicate
                    newDuplicates.push(promoCodeId);
                } else {
                    // First time seeing this cart discount ID
                    seenCartDiscountIds[cartDiscountId] = promoCodeId;
                }
            }
        });

        setDuplicatePromoCodes(newDuplicates);
    }, [usedDiscountCodeIds]);

    return (
        <>
            <Accordion sx={styles.container} allowToggle defaultIndex={defaultIndex}>
                <AccordionItem>
                    {({ isExpanded }) => (
                        <>
                            <AccordionButton sx={styles.accordionButton}>
                                <Text sx={styles.text}>{title || headers.title}</Text>
                                <AccordionIcon />
                            </AccordionButton>
                            <AccordionPanel sx={styles.accordionPanel}>
                                {isExpanded && (
                                    <Flex direction="column" gap={4} mt={4}>
                                        {validPromoCodes.map(({ discountCode }) => {
                                            // Check if the discount code is already applied in cartDiscounts
                                            const isDuplicatePromoCode =
                                                duplicatePromoCodes.includes(discountCode.id);

                                            return (
                                                <PromoCode
                                                    isLocked={isLocked}
                                                    locale={locale}
                                                    discountAmount={getPromoCodeDiscountValue(
                                                        discountCode.id
                                                    )}
                                                    promoCode={discountCode}
                                                    id={discountCode.id}
                                                    key={discountCode.id}
                                                    isPromoRemoving={isPromoRemoving}
                                                    onRemove={onRemove}
                                                    isRemoving={getIsRemoving(discountCode.id)}
                                                    countryISOCode={countryISOCode}
                                                    headers={headers}
                                                    isDuplicatePromoCode={isDuplicatePromoCode} // Pass the availability flag to PromoCode
                                                    setAddedPromocodesErrors={
                                                        setAddedPromocodesErrors
                                                    }
                                                />
                                            );
                                        })}
                                        {shouldShowCodeInput && (
                                            <PromoInput
                                                isLocked={isLocked}
                                                setDraftPromoCode={setDraftPromoCode}
                                                discountCharsLimit={discountCharsLimit}
                                                errorMessage={applyErrorMessage}
                                                isApplying={isApplying}
                                                onApply={onApply}
                                                headers={headers}
                                                setAddedPromocodesErrors={setAddedPromocodesErrors}
                                            />
                                        )}
                                        {addedPromocodesErrors.map((error) => (
                                            <Box
                                                key={error.id}
                                                color={"red.200"}
                                                fontWeight={"semibold"}
                                                fontSize={[
                                                    "mobileBodyTextNormal",
                                                    "desktopBodyTextNormal",
                                                ]}
                                            >
                                                <Flex gap={1} direction="column">
                                                    <Text>{error.code}</Text>
                                                    <Text>{error.message}</Text>
                                                </Flex>
                                            </Box>
                                        ))}
                                    </Flex>
                                )}
                            </AccordionPanel>
                        </>
                    )}
                </AccordionItem>
            </Accordion>
        </>
    );
};

export default PromoCodeRow;
